import type { Dayjs } from 'dayjs';

import dayjs from 'dayjs';

import type { TimezoneDateService } from '@/utils/dateUtils';

import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import pinia from '@/stores/pinia';
import { createDateService, dayjsToDateIgnoreTimezone } from '@/utils/dateUtils';

export function guessTimezone(): string | undefined {
  const guess = dayjs?.tz?.guess();
  // Make sure the timezone is valid (e.g. not Etc/Undefined)
  try {
    dayjs().tz(guess);
    return guess;
  } catch {
    return undefined;
  }
}

export function getTimezoneNames(): string[] {
  return Intl.supportedValuesOf('timeZone') || [];
}

export function getUserTimezone(): string {
  const identityStore = useIdentityStore(pinia);
  // Identity may carry a camelCased `timeZoneName` at runtime that the snake_case type omits.
  const identity = identityStore.identity as
    | (NonNullable<typeof identityStore.identity> & { timeZoneName?: string })
    | null;
  const userTimeZone = identity?.timeZoneName || identity?.time_zone_name;
  return userTimeZone || guessTimezone() || 'UTC';
}

/**
 * The timezone dashing-ui's date components anchor their dates in, or `undefined` while the
 * `dashingUiTimezone` flag is off — which is also what the plugin option itself passes.
 *
 * Every app-side conversion of a picker date has to agree with the components: anchoring
 * app-side in the profile timezone while the components still run browser-local moves the
 * day just as surely as the reverse. Gate both on this, never on the flag directly.
 */
export function getDateComponentTimezone(): string | undefined {
  const flagStore = useFlagStore(pinia);
  return flagStore.ready && flagStore.flags.dashingUiTimezone ? getUserTimezone() : undefined;
}

/**
 * The `dateService` to hand dashing-ui's preset helpers, or `undefined` while the flag is off.
 *
 * Without one a preset resolves in browser time, which the profile-timezone formatting
 * downstream then shifts off by a day; with one while the components are still browser-local,
 * the shift happens in the other direction. Pass this rather than building a service inline.
 */
export function getDateComponentService(): TimezoneDateService | undefined {
  const timezone = getDateComponentTimezone();
  return timezone ? createDateService(timezone) : undefined;
}

/**
 * Convert a dayjs value to the `Date` the date components read back, for feeding them a range
 * bound they did not produce (an "all time" start, a `maxDate`).
 *
 * Once the components read dates in the profile timezone the instant has to stay anchored in
 * it; flattening to browser wall-clock would move the day for anyone whose profile timezone
 * differs from their machine's. While the flag is off the components are still browser-local,
 * so the flattening is what matches them.
 */
export function toDateInUserTz(value: Dayjs): Date {
  return getDateComponentTimezone() ? value.toDate() : dayjsToDateIgnoreTimezone(value);
}

/**
 * Parse a value into a dayjs anchored in the timezone the date components use, for doing
 * day-boundary math (`startOf`/`endOf`) on a picker date.
 */
export function dayjsInUserTz(value?: dayjs.ConfigType): Dayjs {
  const timezone = getDateComponentTimezone();
  return timezone ? dayjs(value).tz(timezone) : dayjs(value);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a date-range value as a date-only string in the timezone the date components use.
 *
 * dashing-ui's date components anchor midnight in the profile timezone, so formatting
 * their output with browser-local dayjs shifts the day whenever the two differ.
 * Use this for every picker-range → query-param/API date string conversion.
 *
 * A `YYYY-MM-DD` input already names a calendar day, so it is anchored rather than
 * converted — round-tripping an already-serialized range must not move it.
 */
export function formatDateInUserTz(date: dayjs.ConfigType, pattern = 'YYYY-MM-DD'): string {
  const timezone = getDateComponentTimezone();
  if (!timezone) return dayjs(date).format(pattern);
  const value =
    typeof date === 'string' && DATE_ONLY.test(date)
      ? dayjs.tz(date, timezone)
      : dayjs(date).tz(timezone);
  return value.format(pattern);
}

/**
 * Parse a date-only string into the `Date` at midnight in the timezone the date components
 * use — the inverse of {@link formatDateInUserTz}, for hydrating a picker range from a query
 * param.
 *
 * Returns an invalid `Date` for unparseable input: these values come from URLs, and
 * `dayjs.tz` throws `RangeError` on them rather than yielding `Invalid Date`.
 */
export function parseDateInUserTz(value: string, pattern = 'YYYY-MM-DD'): Date {
  if (!dayjs(value, pattern).isValid()) return new Date(NaN);
  const timezone = getDateComponentTimezone();
  if (!timezone) return dayjs(value, pattern).toDate();
  return dayjs.tz(value, pattern, timezone).toDate();
}
